import type { Board } from "../board";
import type { Direction } from "../direction";
import { directionDelta } from "../direction";
import { MovementEffect } from "../effects/movementEffect";
import {
	PRIORITY_FIRST,
	isProjectileHittable,
	type ParticleEmitter,
	type ProcessableThing,
	type SomethingWithState,
} from "../interfaces";
import { ParticleType } from "../particleType";
import { PhysicalThing } from "../physicalThing";
import { deserializeXY, serializeXY } from "../serialization";
import { Gas } from "../things/gas";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";

enum GasBombState {
	TRAVELLING = "TRAVELLING",
	RELEASING = "RELEASING",
	EMPTY = "EMPTY",
}

export class GasBomb extends PhysicalThing implements ProcessableThing, SomethingWithState, ParticleEmitter {
	static key = "GasBomb";
	private state: GasBombState = GasBombState.TRAVELLING;
	private direction: Direction;
	private range: number = 10;
	private compression: number = 2500;
	private releasingXY: XY | null = null;
	private static readonly RELEASE_RATE = 100;

	constructor(xy: XY, direction: Direction) {
		super(xy, false);
		this.direction = direction;
	}

	getZOrder(): number {
		return ZOrder.PROJECTILE;
	}

	getState(): string {
		return this.state === GasBombState.RELEASING ? "releasing" : "travelling";
	}

	getParticleType(): ParticleType | null {
		return this.state === GasBombState.RELEASING ? ParticleType.GAS_LEAK : null;
	}

	processTurn(board: Board): void {
		if (this.state === GasBombState.TRAVELLING) {
			this.range--;
			if (this.range <= 0) {
				this.blast(board, this.xy);
			} else {
				this.travel(board);
			}
		}

		if (this.state === GasBombState.RELEASING) {
			this.releaseGas(board);
		}

		if (this.state === GasBombState.EMPTY) {
			board.removeAtEndOfTurn(this);
		}
	}

	private travel(board: Board): void {
		const oldXY = this.xy;
		const destination = this.xy.add(directionDelta(this.direction));

		if (
			GasBomb.collisionCheck(board, this.xy) ||
			(board.isInBounds(destination) && GasBomb.collisionCheck(board, destination))
		) {
			this.blast(board, this.xy);
			return;
		}

		if (!board.isInBounds(destination)) {
			board.removeAtEndOfTurn(this);
			return;
		}

		this.xy = destination;
		board.addEffect(new MovementEffect(this, oldXY, destination));
	}

	static collisionCheck(board: Board, targetXY: XY): boolean {
		if (!board.isInBounds(targetXY)) return false;
		return board.getThingsAt(targetXY).some((t) => isProjectileHittable(t) && t.isHittable());
	}

	private releaseGas(board: Board): void {
		const releasedAmount = Math.min(GasBomb.RELEASE_RATE, this.compression);
		this.compression -= releasedAmount;

		const existingGas = board.getThingOfClass(Gas, this.releasingXY!);
		if (existingGas !== null) {
			existingGas.setCompression(existingGas.getCompression() + releasedAmount);
		} else {
			const gas = new Gas(this.releasingXY!, releasedAmount);
			board.addThing(gas);
		}

		if (this.compression <= 0) {
			this.state = GasBombState.EMPTY;
		}
	}

	private blast(board: Board, blastXY: XY): void {
		if (!board.isPassable(blastXY)) {
			board.removeAtEndOfTurn(this);
			return;
		}
		this.releasingXY = blastXY;
		this.state = GasBombState.RELEASING;
	}

	getProcessPriority(): number {
		return PRIORITY_FIRST + 1;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			state: this.state,
			direction: this.direction,
			range: this.range,
			compression: this.compression,
			releasingXY: this.releasingXY ? serializeXY(this.releasingXY) : null,
		};
	}

	static deserialize(data: any): GasBomb {
		const g = new GasBomb(deserializeXY(data.xy), data.direction);
		g.visualId = data.visualId;
		g.state = data.state;
		g.range = data.range;
		g.compression = data.compression;
		if (data.releasingXY) g.releasingXY = deserializeXY(data.releasingXY);
		return g;
	}
}
